import React, { lazy, Suspense } from 'react'
import { Navigate, useRoutes } from 'react-router-dom'
import { AppLayout, LoginLayout } from 'components/layout'

const Login = lazy(() => import('views/auth/Login'))
const ForgotPassword = lazy(() => import('views/auth/ForgotPassword'))
const ResetPassword = lazy(() => import('views/auth/ResetPassword'))
const Error = lazy(() => import('views/Error'))
const Dashboard = lazy(() => import('views/dashboard'))
const BillingSummary = lazy(() => import('views/loyalty/BillingSummary'))
const CafeStats = lazy(() => import('views/loyalty/CafeStats'))
const AdvancedCafeStats = lazy(() => import('views/loyalty/AdvancedCafeStats'))
const LoyaltyNotifications = lazy(() => import('views/loyalty/LoyaltyNotifications'))
const LoyaltyNotificationsSelector = lazy(() => import('views/loyalty/LoyaltyNotificationsSelector'))
const LoyaltySettings = lazy(() => import('views/loyalty/LoyaltySettings'))
const LoyaltyTiers = lazy(() => import('views/loyalty/LoyaltyTiers'))
const NfcCards = lazy(() => import('views/loyalty/nfc-cards'))
const Profile = lazy(() => import('views/auth/Profile'))
const Company = lazy(() => import('views/companies'))
const CreateComapny = lazy(() => import('views/companies/create'))
const Branch = lazy(() => import('views/branch/all-branches'))
const CreateBranch = lazy(() => import('views/branch/all-branches/create'))
const Features = lazy(() => import('views/branch/features'))
const CreateFeatures = lazy(() => import('views/branch/features/create'))
const Services = lazy(() => import('views/branch/services'))
const CreateServices = lazy(() => import('views/branch/services/create'))
const Timings = lazy(() => import('views/branch/branchTimings'))
const CreateTimings = lazy(() => import('views/branch/branchTimings/create'))
const Holidays = lazy(() => import('views/branch/holidays'))
const CreateHoliday = lazy(() => import('views/branch/holidays/create'))
const Role = lazy(() => import('views/roles'))
const CreateRoles = lazy(() => import('views/roles/create'))
const Admins = lazy(() => import('views/admins'))
const CreateAdmins = lazy(() => import('views/admins/create'))
const Users = lazy(() => import('views/users'))
const CreateUsers = lazy(() => import('views/users/create'))
const Category = lazy(() => import('views/products/categories'))
const CreateCategory = lazy(() => import('views/products/categories/create'))
const ProductType = lazy(() => import('views/products/product-type'))
const CreateProductType = lazy(() => import('views/products/product-type/create'))
const Tax = lazy(() => import('views/products/tax'))
const CreateTax = lazy(() => import('views/products/tax/create'))
const ProductCategories = lazy(() => import('views/products/product-categories'))
const CreateProductCategory = lazy(() => import('views/products/product-categories/create'))
const Products = lazy(() => import('views/products/all-products'))
const CreateProduct = lazy(() => import('views/products/all-products/create'))
const ProductSubs = lazy(() => import('views/products/product-subs'))
const CreateProductSubs = lazy(() => import('views/products/product-subs/create'))
const Orders = lazy(() => import('views/orders'))
const CreateOrders = lazy(() => import('views/orders/create'))
const Subscription = lazy(() => import('views/subscription'))
const CreateSubscription = lazy(() => import('views/subscription/create'))
const CreateBulkSubscription = lazy(() => import('views/subscription/bulk_create'))
const AssignSubscription = lazy(() => import('views/assign-subscription'))
const CreateAssignSubscription = lazy(() => import('views/assign-subscription/create'))
const ManageProductSubscription = lazy(() => import('views/manage-product-subscription'))
const CreateManageProductSubscription = lazy(() => import('views/manage-product-subscription/create'))
const Stripe = lazy(() => import('views/stripe'))
const CreateStripe = lazy(() => import('views/stripe/create'))
const Coupons = lazy(() => import('views/coupons'))
const CreateCoupons = lazy(() => import('views/coupons/create'))
const RatingsReviews = lazy(() => import('views/ratings-reviews'))
const Inquiry = lazy(() => import('views/inquiry'))
const CreateInquiry = lazy(() => import('views/inquiry/create'))
const FaqList = lazy(() => import('views/faq'))
const CreateFaq = lazy(() => import('views/faq/create'))
const AddOns = lazy(() => import('views/addons'))
const NotificationStats = lazy(() => import('views/admin/notifications'))
const Permissions = lazy(() => import('views/permissions'))

function Loader() {
    return (
        <div className="flex items-center justify-center h-64">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
    )
}

function Lazy({ children }) {
    return <Suspense fallback={<Loader />}>{children}</Suspense>
}

function sharedRoutes(prefix, { list: List, create: Create, parent, suffix } = {}) {
    const p = parent || prefix
    return ['', '/create', '/:id/edit'].map(s => ({
        path: `${prefix}${s}`,
        element: <Lazy>{s === '/create' || s === '/:id/edit' ? <Create /> : <List />}</Lazy>,
    }))
}

export default function Routes() {
    return useRoutes([
        {
            element: <AppLayout />,
            children: [
                { path: '/', element: <Navigate to="/dashboard" replace /> },
                { path: '/dashboard', element: <Lazy><Dashboard /></Lazy> },
                { path: '/profile', element: <Lazy><Profile /></Lazy> },
                ...sharedRoutes('/subscription', { list: Subscription, create: CreateSubscription }),
                { path: '/subscription/:id/edit', element: <Lazy><CreateBulkSubscription /></Lazy> },
                { path: '/subscription/bulk_create', element: <Lazy><CreateBulkSubscription /></Lazy> },
                ...sharedRoutes('/assign-subscription', { list: AssignSubscription, create: CreateAssignSubscription }),
                { path: '/assign-subscription/bulk_create', element: <Lazy><CreateBulkSubscription /></Lazy> },
                ...sharedRoutes('/manage-product-subscription', { list: ManageProductSubscription, create: CreateManageProductSubscription }),
                ...sharedRoutes('/stripe', { list: Stripe, create: CreateStripe }),
                ...sharedRoutes('/companies', { list: Company, create: CreateComapny }),
                ...sharedRoutes('/branch/all-branches', { list: Branch, create: CreateBranch }),
                ...sharedRoutes('/branch/features', { list: Features, create: CreateFeatures }),
                ...sharedRoutes('/branch/services', { list: Services, create: CreateServices }),
                ...sharedRoutes('/branch/branch-timings', { list: Timings, create: CreateTimings }),
                ...sharedRoutes('/branch/holidays', { list: Holidays, create: CreateHoliday }),
                ...sharedRoutes('/roles', { list: Role, create: CreateRoles }),
                { path: '/assign-permissions', element: <Lazy><Permissions /></Lazy> },
                ...sharedRoutes('/admins', { list: Admins, create: CreateAdmins }),
                ...sharedRoutes('/users', { list: Users, create: CreateUsers }),
                {
                    path: '/product',
                    children: [
                        ...sharedRoutes('categories', { list: Category, create: CreateCategory }),
                        ...sharedRoutes('product-type', { list: ProductType, create: CreateProductType }),
                        ...sharedRoutes('tax', { list: Tax, create: CreateTax }),
                        ...sharedRoutes('product-categories', { list: ProductCategories, create: CreateProductCategory }),
                        ...sharedRoutes('all-products', { list: Products, create: CreateProduct }),
                        ...sharedRoutes('product-subs', { list: ProductSubs, create: CreateProductSubs }),
                    ],
                },
                ...sharedRoutes('/orders', { list: Orders, create: CreateOrders }),
                ...sharedRoutes('/coupons', { list: Coupons, create: CreateCoupons }),
                { path: '/ratings-reviews', element: <Lazy><RatingsReviews /></Lazy> },
                ...sharedRoutes('/inquiry', { list: Inquiry, create: CreateInquiry }),
                ...sharedRoutes('/faq', { list: FaqList, create: CreateFaq }),
                {
                    path: '/loyalty',
                    children: [
                        { path: '', element: <Lazy><BillingSummary /></Lazy> },
                        { path: 'settings', element: <Lazy><LoyaltySettings /></Lazy> },
                        { path: 'nfc-cards', element: <Lazy><NfcCards /></Lazy> },
                        { path: 'cafes/:id', element: <Lazy><CafeStats /></Lazy> },
                        { path: 'advanced-analytics/:id', element: <Lazy><AdvancedCafeStats /></Lazy> },
                        { path: 'notifications', element: <Lazy><LoyaltyNotificationsSelector /></Lazy> },
                        { path: 'notifications/:id', element: <Lazy><LoyaltyNotifications /></Lazy> },
                        { path: 'tiers', element: <Lazy><LoyaltyTiers /></Lazy> },
                    ],
                },
                { path: '/addons', element: <Lazy><AddOns /></Lazy> },
                { path: '/admin/notifications', element: <Lazy><NotificationStats /></Lazy> },
            ],
        },
        {
            element: <LoginLayout />,
            children: [
                { path: '/login', element: <Lazy><Login /></Lazy> },
                { path: '/forgot-password', element: <Lazy><ForgotPassword /></Lazy> },
                { path: '/userauth/reset-password/:uid/:token', element: <Lazy><ResetPassword /></Lazy> },
            ],
        },
        { path: '/error', element: <Lazy><Error /></Lazy> },
        { path: '*', element: <Navigate to="/error" replace /> },
    ])
}
